import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, TextField, Typography, Hidden, FormControl, InputLabel,
    MenuItem, Select
} from '@mui/material';
import { Search as IconSearch } from "@mui/icons-material";
import { setResults, setValue } from '../redux/personSearchSlice';
import { PaperComponentDraggable } from "./common/PaperComponentDraggable";
import * as personaService from "../services/personaService";
import DataGridBase from "./common/DataGridBase";

/**
 * Ventana estándar y única de búsqueda de personas en el sistema.
 * Filtros:
 * - Número de documento
 * - Nombres
 * - Empresa: Todos (""), Sí ("S"), No ("N")
 */
const PersonSearch = (props) => {
    const { open, empresa } = props;
    const handleClose = props.openClose || props.onClose;
    const handleSelect = props.setSelection || props.onAceptar || props.onSelect;

    const filters = useSelector((state) => state.personSearch?.filters) || {
        numeroDocumento: "",
        nombres: "",
        empresa: "",
    };
    const results = useSelector((state) => state.personSearch?.results) || [];
    const dispatch = useDispatch();

    // Fila resaltada con un clic. Se elige con doble clic o con el botón Aceptar.
    const [filaElegida, setFilaElegida] = useState(null);
    const [cargando, setCargando] = useState(false);
    const dialogRef = useRef();
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        // La ventana abre limpia: sin fila resaltada de la búsqueda anterior.
        setFilaElegida(null);

        // Si el invocador fija el tipo (ej. empresa="N" en vinculaciones), se aplica ese filtro.
        if (empresa !== undefined) {
            dispatch(setValue({ name: "empresa", value: empresa ?? "" }));
        }

        setTimeout(() => inputRef.current?.focus(), 250);
    }, [open, empresa, dispatch]);

    const fetchResults = useCallback(async () => {
        setCargando(true);
        try {
            const response = await personaService.getPersonaResumenBasicoViewByNumeroDocumentoYNombres(
                filters.numeroDocumento ?? "",
                filters.nombres ?? "",
                filters.empresa ?? ""
            );
            const lista = Array.isArray(response)
                ? response
                : (response?.data?.data || response?.data || []);
            dispatch(setResults(lista));
        } catch (err) {
            console.error("Error al buscar personas:", err);
            dispatch(setResults([]));
        } finally {
            setFilaElegida(null);
            setCargando(false);
        }
    }, [dispatch, filters.numeroDocumento, filters.nombres, filters.empresa]);

    const seleccionar = (fila) => {
        if (!fila) return;
        if (handleSelect) handleSelect(fila);
        if (handleClose) handleClose();
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 60, sortable: true, headerAlign: 'center', align: 'center', ocultarConAuditoria: false },
        { field: 'nombres', headerName: 'Nombres', width: 240, sortable: true },
        { field: 'tipoDocumento', headerName: 'Tip.Doc.', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'numeroDocumento', headerName: 'Num.Doc.', width: 110, sortable: false, headerAlign: 'center', align: 'center' },
        {
            field: 'esEmpresa',
            headerName: 'Empresa',
            width: 85,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (params) => {
                const val = params.value ?? params.row?.empresa ?? params.row?.esEmpresa;
                return (val === true || val === "True" || val === "true" || val === "S" || val === "s" || val === 1 || val === "1") ? "S" : "N";
            },
            renderCell: (params) => {
                const val = params.value ?? params.row?.empresa ?? params.row?.esEmpresa;
                return (val === true || val === "True" || val === "true" || val === "S" || val === "s" || val === 1 || val === "1") ? "S" : "N";
            }
        },
    ];

    return (
        <Dialog
            open={!!open}
            onClose={handleClose}
            fullWidth={true}
            maxWidth={"md"}
            ref={dialogRef}
            PaperComponent={PaperComponentDraggable}
        >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Buscar Persona
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ minHeight: "350px", maxHeight: "70vh", overflowY: "auto" }}>
                <Grid container item xs={12} sm={12} spacing={1} padding={1} alignItems="center">
                    <Grid item xs={6} sm={3}>
                        <TextField
                            name="numeroDocumento"
                            label="Num. Doc."
                            value={filters.numeroDocumento || ""}
                            fullWidth
                            onChange={(event) => {
                                dispatch(setValue({ name: "numeroDocumento", value: event.target.value }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    fetchResults();
                                }
                            }}
                            size="small"
                            inputRef={inputRef}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <TextField
                            name="nombres"
                            label="Nombres"
                            value={filters.nombres || ""}
                            fullWidth
                            onChange={(event) => {
                                dispatch(setValue({ name: "nombres", value: event.target.value }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    fetchResults();
                                }
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Empresa</InputLabel>
                            <Select
                                label="Empresa"
                                value={filters.empresa ?? ""}
                                onChange={(event) => {
                                    dispatch(setValue({ name: "empresa", value: event.target.value }));
                                }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="S">Sí</MenuItem>
                                <MenuItem value="N">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2} display="flex" alignItems="center">
                        <Button variant="outlined" fullWidth onClick={fetchResults}>
                            {<Hidden smDown>Buscar</Hidden>}
                            <IconSearch />
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <DataGridBase
                            rows={results}
                            columns={columns}
                            loading={cargando}
                            paginationTop={false}
                            pageSize={5}
                            pageSizeOptions={[5, 10, 20, 50]}
                            disableRowSelectionOnClick={false}
                            onRowClick={(params) => setFilaElegida(params.row)}
                            onRowDoubleClick={(params) => seleccionar(params.row)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!filaElegida}
                    onClick={() => seleccionar(filaElegida)}
                >
                    Aceptar
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PersonSearch;
